import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, Print as PrintIcon, ReceiptLong as ReceiptLongIcon } from '@mui/icons-material';
import { openVoucherPrint } from '../../services/expenseOperationsApi';
import {
  vouchersApi,
  formatAmount,
  formatDate,
  formatDateTime,
  formatTotals,
} from '../../services/expenseVouchersApi';
import { toast } from 'react-toastify';
import {
  useExpenseBase, VoucherStatusChip, PaymentStatusChip, vendorLabel, historyLabel, paymentTypeLabel,
} from '../../components/expense/voucherShared';
import SettlementSummary from '../../components/expense/SettlementSummary';
import BillList from '../../components/expense/BillList';
import ApprovalProgress from '../../components/expense/ApprovalProgress';

const Info = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body1" component="div">{children || '-'}</Typography>
  </Grid>
);

const headerCell = { color: 'white', fontWeight: 600, whiteSpace: 'nowrap' };

// Read-only voucher view (any status). Edit is offered only while the voucher is editable.
const ExpenseVoucherDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const base = useExpenseBase();
  const [voucher, setVoucher] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setVoucher(null);
    setError(null);
    vouchersApi.get(id)
      .then((res) => { if (!cancelled) setVoucher(res.data); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [id]);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={error.status === 404 || error.status === 403 ? 'warning' : 'error'}>
          {error.status === 404 ? 'Voucher not found.' : error.message}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(base)} sx={{ mt: 2, textTransform: 'none' }}>Back to My Expenses</Button>
      </Box>
    );
  }

  if (!voucher) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  const submission = [...(voucher.history || [])].reverse().find((h) => h.action === 'SUBMITTED' || h.action === 'RESUBMITTED');
  const latest = (status) => [...(voucher.history || [])].reverse().find((h) => h.toStatus === status);
  const sentBack = voucher.status === 'SENT_BACK' ? latest('SENT_BACK') : null;
  const rejected = voucher.status === 'REJECTED' ? latest('REJECTED') : null;
  const approved = voucher.status === 'APPROVED' ? latest('APPROVED') : null;

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLongIcon color="primary" />
            Expense {voucher.voucherNumber}
            <VoucherStatusChip status={voucher.status} size="medium" />
            <PaymentStatusChip status={voucher.paymentStatus} settledVia={voucher.settledVia} size="medium" />
          </Typography>
        </Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(base)} sx={{ textTransform: 'none' }}>My Expenses</Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => openVoucherPrint(voucher._id)} sx={{ textTransform: 'none' }}>Print / PDF</Button>
        {voucher.canEdit && (
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`${base}/${voucher._id}/edit`)} sx={{ textTransform: 'none' }}>
            Edit
          </Button>
        )}
      </Box>

      <SettlementSummary settlement={voucher.settlement} />
      {voucher.advanceSettlement && (
        <Alert
          severity={voucher.advanceSettlement.settledBy ? 'success' : 'info'}
          sx={{ mb: 2 }}
          action={voucher.advanceSettlement.canCreateSettlement && (
            <Button
              color="inherit"
              size="small"
              onClick={async () => {
                try {
                  const res = await vouchersApi.createSettlement(voucher._id);
                  navigate(`${base}/${res.data._id}/edit`);
                } catch (e) {
                  toast.error(e.message);
                }
              }}
              sx={{ textTransform: 'none' }}
            >
              {voucher.advanceSettlement.draft ? 'Continue settlement' : 'Create settlement'}
            </Button>
          )}
        >
          {voucher.advanceSettlement.settledBy
            ? `This advance is settled by ${voucher.advanceSettlement.settledBy.voucherNumber}.`
            : voucher.advanceSettlement.draft
              ? `Settlement draft ${voucher.advanceSettlement.draft.voucherNumber} has not been submitted yet; the advance is still unsettled.`
              : 'This advance has not been settled yet.'}
        </Alert>
      )}
      {sentBack && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Sent back by {sentBack.byName || 'Accounts'} on {formatDateTime(sentBack.at)}.{sentBack.comment ? ` Comment: ${sentBack.comment}` : ''}
        </Alert>
      )}
      {voucher.status === 'PENDING_ACCOUNTS' && (
        <Alert severity="info" sx={{ mb: 2 }}>Submitted to Accounts. This voucher can no longer be edited.</Alert>
      )}
      {voucher.status === 'PENDING_APPROVAL' && (
        <Alert severity="info" sx={{ mb: 2 }}>Accounts has sent this voucher to your approvers.</Alert>
      )}
      {approved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Approved by {approved.byName || 'Accounts'} on {formatDateTime(approved.at)}.{approved.comment ? ` Note: ${approved.comment}` : ''}
        </Alert>
      )}
      {rejected && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Rejected by {rejected.byName || 'an approver'} on {formatDateTime(rejected.at)}. Reason: {rejected.comment}
          <br />A rejected voucher cannot be edited or resubmitted. Create a new voucher if needed.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Voucher information</Typography>
        <Grid container spacing={2}>
          <Info label="Voucher Number">{voucher.voucherNumber}</Info>
          <Info label="Voucher Date">{formatDate(voucher.voucherDate)}</Info>
          <Info label="Invoice Number">{voucher.invoiceNumber}</Info>
          <Info label="Total">{formatTotals(voucher)}</Info>
          <Info label="Employee">{voucher.employeeSnapshot?.name}</Info>
          <Info label="Department">{voucher.employeeSnapshot?.department}</Info>
          <Info label="Submitted">{voucher.submittedAt ? `${formatDateTime(voucher.submittedAt)}${submission?.byName ? ` by ${submission.byName}` : ''}` : 'Not submitted'}</Info>
          <Info label="Status"><VoucherStatusChip status={voucher.status} /></Info>
        </Grid>
      </Paper>

      <Typography variant="h6" sx={{ mb: 1 }}>Expense lines</Typography>
      <TableContainer component={Paper} sx={{ boxShadow: 2, mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {['Date', 'Vendor', 'Category / Head', 'Employment Country', 'Expense Country', 'Payment Type', 'Description', 'Amount', 'Bills'].map((h) => (
                <TableCell key={h} sx={headerCell}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!voucher.lines.length && (
              <TableRow><TableCell colSpan={9} align="center"><Typography variant="body2" color="text.secondary">No expense lines</Typography></TableCell></TableRow>
            )}
            {voucher.lines.map((l) => (
              <TableRow key={l._id}>
                <TableCell>{formatDate(l.expenseDate)}</TableCell>
                <TableCell>{vendorLabel(l.vendor)}</TableCell>
                <TableCell>{[l.categoryName, l.headName].filter(Boolean).join(' / ') || '-'}</TableCell>
                <TableCell>{l.employmentCountryName || '-'}</TableCell>
                <TableCell>{l.expenseCountryName || '-'}</TableCell>
                <TableCell>{paymentTypeLabel(l)}</TableCell>
                <TableCell sx={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>{l.description || '-'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{formatAmount(l.amount, l.currency)}</TableCell>
                <TableCell><BillList voucherId={voucher._id} bills={l.bills} /></TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={8} align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
              <TableCell colSpan={2} sx={{ fontWeight: 600 }}>{formatTotals(voucher)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {(voucher.requiredGroups || []).length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Approvals</Typography>
          <ApprovalProgress voucher={voucher} />
        </Paper>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>History</Typography>
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {['Date / Time', 'By', 'Action', 'Comment'].map((h) => <TableCell key={h} sx={headerCell}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...(voucher.history || [])].reverse().map((h, i) => (
              <TableRow key={`${h.at}-${i}`}>
                <TableCell>{formatDateTime(h.at)}</TableCell>
                <TableCell>{h.byName || '-'}</TableCell>
                <TableCell>{historyLabel(h)}</TableCell>
                <TableCell>{h.comment || ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ExpenseVoucherDetails;
